import { Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { LenderService } from '@shared/service/lender/lender.service';
import { BundleHelperService } from '@shared/service/bundle-helper.service';
import { AdminPortalFeaturesService } from '@workflow/ecp-shared/service/user/admin-portal-features.service';
import { ApplicationApiService } from '@workflow/ecp-workflow/dynamic-app/service/application-api/application-api.service';
import { L360Service } from '@workflow/ecp-workflow/dynamic-app/service/l360/l360.service';
import { Product, ApplicantProduct } from '@workflow/ecp-workflow/dynamic-app/types/accounts-and-services.type';
import { firstValueFrom } from 'rxjs';
import { StatefulService } from '@app/service/stateful.service';
/** OAO checking and saving accounts component */
@Component({
  selector: 'ecp-oao-checking-and-saving-account',
  templateUrl: './oao-checking-saving-account.component.html'
})
export class OaoCheckingAndSavingAccountComponent implements OnInit {
  private readonly statefulService = inject(StatefulService);

  /** oao account objcet */
  @Input() public mainAccount: Product[];

  /** all account products */
  @Input() public allProducts: Product[];

  /** Callback Parent method to enable the continue button if all validations are valid */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Output() public enableContinueButton = new EventEmitter<any>();

  /** co Applicant Products */
  @Input() public coApplicantProducts: ApplicantProduct[];

  /** Is Initial Loading from L360 Call */
  @Input() public isInitialLoading: boolean;

  /** Co Applicants Share : Local Variable */
  public coAppProducts: ApplicantProduct[];

  /** showing checking account flag */
  public showAccounts = false;

  /** lender config data */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public lenderConfig: any = {};

  constructor(
    public readonly applicationApiService: ApplicationApiService,
    public readonly l360Service: L360Service,
    private readonly bundleHelperService: BundleHelperService,
    public readonly lenderService: LenderService,
    public readonly adminPortalFeaturesService: AdminPortalFeaturesService
  ) {
    this.coAppProducts = [];
  }

  public ngOnInit(): void {
    this.setLenderData();
    this.coAppProducts = this.coApplicantProducts.map((x) => Object.assign({}, x));
    // Set accordian Open status if any required field is active
    const isAnyShareOptOut = this.mainAccount.filter((f) => f.requiredOptInType.toLowerCase() === 'opt-out').length > 0 ? true : false;
    let isAnyServiceOptOut = false;
    this.mainAccount.forEach((share) => {
      share.optionalServices.forEach((service) => {
        if (service.requiredOptInType.toLowerCase() === 'opt-out') {
          isAnyServiceOptOut = true;
        }
      });
    });
    if ((isAnyShareOptOut || isAnyServiceOptOut) && this.isInitialLoading) {
      this.showAccounts = true;
    }
  }

  /** Set Lender Data */
  public setLenderData(): void {
    this.lenderConfig = this.lenderService.lenderDataInfo;
  }

  /** get acocunt title */
  public get title(): string {
    return this.mainAccount.length > 0 ? this.mainAccount[0].typeName : '';
  }

  /** get/find Is this the checking account? */
  public get isChecking(): boolean {
    return this.mainAccount.length > 0 && this.mainAccount[0].type.toLowerCase() === 'checking';
  }

  /** When primary account's selection is unchecked, clear all co applicant's shares */
  public primaryShareSelection(account: Product): void {
    // Clear primary Applicants services. When remove primar applicant's share, then clear all the services under the share
    if (!account.isSelected && account.optionalServices) {
      account.optionalServices.forEach((element) => {
        element.isSelected = false;
      });
    }

    // Clear Co-Applicants Shares. When remove primar applicant's share, then clear all the co applicant's share & services
    if (!account.isSelected) {
      this.coAppProducts.forEach((applicant) => {
        applicant.shares
          .filter((f) => f.id === account.id)
          .forEach((share) => {
            share.isSelected = false;
            share.isDisabled = false;
            share.optionalServices.forEach((service) => {
              service.isSelected = false;
            });
          });
      });
    } else {
      if (this.isYouthMembership()) {
        // If youth membership, then whatever share selected by Primary Applicant applicable for all co applicant's
        this.coAppProducts.forEach((applicant) => {
          applicant.shares
            .filter((f) => f.id === account.id)
            .forEach((share) => {
              share.isSelected = true;
              share.isDisabled = true;
            });
        });
      }
    }
    this.enableContinueButton.next({ page: 'checking', valid: true });
    this.UpdateBundleIndex();
    this.updateSelection();
  }

  // On Selection of Co Applicant's Share checkbox
  public coAppShareSelection(parentAccount: Product, coApplicantProduct: ApplicantProduct): void {
    const shareId = parentAccount.id;
    const isSelected = coApplicantProduct.shares.filter((f) => f.id === shareId)[0].isSelected;
    const rawReqBody = [];

    // Set Co Applicant Share Selection Status
    rawReqBody.push(this.generateRequestBody(coApplicantProduct.bundlePath, parentAccount.bundleIndex, 'isSelected', isSelected, null));

    // Iterate the co applicant's Optional Service & set its visibility. If co
    // app share selected then make respective applicant's services visible
    coApplicantProduct.shares
      // eslint-disable-next-line eqeqeq
      .filter((f) => f.id == shareId)
      .forEach((Share) => {
        Share.optionalServices.forEach((service) => {
          service.isVisible = isSelected;
          rawReqBody.push(
            this.generateRequestBody(coApplicantProduct.bundlePath, Share.bundleIndex, 'isVisible', service.isVisible, service.bundleIndex)
          );
          if (!isSelected) {
            service.isSelected = isSelected;
            rawReqBody.push(
              this.generateRequestBody(
                coApplicantProduct.bundlePath,
                Share.bundleIndex,
                'isSelected',
                service.isSelected,
                service.bundleIndex
              )
            );
          }
        });
      });

    // Check whether max allowed applicant selection met or not, if so, then disable other shares
    const maxAllowedApplicant = parentAccount.numberOfJointApplicants;
    let selectedCoApp = 0;

    // Count Total Selected co Applicant Share
    this.coAppProducts.forEach((applicant) => {
      applicant.shares
        .filter((f) => f.id === shareId)
        .forEach((item) => {
          selectedCoApp = item.isSelected ? selectedCoApp + 1 : selectedCoApp;
        });
    });

    // If Max Allowed Co App selection equals to selected co app shares then disable remaing co applicant
    if (selectedCoApp === maxAllowedApplicant) {
      this.coAppProducts.forEach((applicant) => {
        applicant.shares
          .filter((f) => f.id === shareId)
          .forEach((share) => {
            share.isDisabled = share.isSelected ? false : true;
            rawReqBody.push(this.generateRequestBody(applicant.bundlePath, share.bundleIndex, 'isDisabled', share.isDisabled, null));
            if (share.isDisabled) {
              share.optionalServices.forEach((service) => {
                service.isVisible = false;
                rawReqBody.push(this.generateRequestBody(applicant.bundlePath, share.bundleIndex, 'isVisible', false, service.bundleIndex));
              });
            }
          });
      });
    } else {
      this.coAppProducts.forEach((applicant) => {
        applicant.shares
          .filter((f) => f.id === shareId)
          .forEach((share) => {
            share.isDisabled = false;
            rawReqBody.push(this.generateRequestBody(applicant.bundlePath, share.bundleIndex, 'isDisabled', share.isDisabled, null));
            share.optionalServices.forEach((service) => {
              service.isVisible = share.isSelected;
              rawReqBody.push(
                this.generateRequestBody(applicant.bundlePath, share.bundleIndex, 'isVisible', share.isSelected, service.bundleIndex)
              );
            });
          });
      });
    }

    setTimeout(() => {
      firstValueFrom(this.applicationApiService.bundle(rawReqBody))
        .then(() => {})
        .catch(() => {});
    }, 0);
    this.enableContinueButton.next({ page: 'checking', valid: true });
    this.updateSelection();
  }

  // Save/Remove data into bundle when optional services selected/removed
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public setOptionalServiceSelection(account: Product, option: any): void {
    const rawReqBody = [];

    // set Parent Applicant - Optional Serices
    rawReqBody.push(this.generateRequestBody('primaryApplicant', account.bundleIndex, 'isSelected', option.isSelected, option.bundleIndex));

    // Set Co Applicants - Selection
    // If Primary Applicant's Service Selection is False, then make co applicant's service selectiuon as well false
    this.coAppProducts.forEach((applicant) => {
      applicant.shares
        .filter((f) => f.id === account.id)
        .forEach((share) => {
          share.optionalServices
            .filter((o) => o.name === option.name)
            .forEach((service) => {
              if (!option.isSelected) {
                service.isSelected = false;
                rawReqBody.push(
                  this.generateRequestBody(applicant.bundlePath, share.bundleIndex, 'isSelected', service.isSelected, service.bundleIndex)
                );
              } else if (
                option.isSelected &&
                (account.numberOfJointApplicants === 0 || this.isYouthMembership()) &&
                service.availableForJointOwner
              ) {
                service.isSelected = true;
                if (this.isYouthMembership()) {
                  service.isVisible = true;
                  rawReqBody.push(
                    this.generateRequestBody(applicant.bundlePath, share.bundleIndex, 'isVisible', service.isVisible, service.bundleIndex)
                  );
                }
                rawReqBody.push(
                  this.generateRequestBody(applicant.bundlePath, share.bundleIndex, 'isSelected', service.isSelected, service.bundleIndex)
                );
              }
            });
        });
    });

    setTimeout(() => {
      firstValueFrom(this.applicationApiService.bundle(rawReqBody))
        .then(() => {})
        .catch(() => {});
    }, 0);
    this.updateSelection();
  }

  /** Co Applicant's Service Selection */
  public coAppServiceSelection(bundlePath: string, shareBundleIndex: number, serviceBundleIndex: number, value: boolean): void {
    const rawReqBody = [];
    rawReqBody.push(this.generateRequestBody(bundlePath, shareBundleIndex, 'isSelected', value, serviceBundleIndex));
    setTimeout(() => {
      firstValueFrom(this.applicationApiService.bundle(rawReqBody))
        .then(() => {})
        .catch(() => {});
    }, 0);

    this.updateSelection();
  }

  /** Generate Request Body */
  public generateRequestBody(
    shareBundlePath: string,
    shareBundleIndex: number,
    propName: string,
    value: boolean,
    serviceBundleIndex: number = null
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ): any {
    if (serviceBundleIndex === null) {
      return {
        op: 'add',
        path: `oaoMemberInformation/accountsAndServices/${shareBundlePath}/shares/${shareBundleIndex}/${propName}`,
        value
      };
    } else {
      return {
        op: 'add',
        // eslint-disable-next-line max-len
        path: `oaoMemberInformation/accountsAndServices/${shareBundlePath}/shares/${shareBundleIndex}/OptionalServices/${serviceBundleIndex}/${propName}`,
        value
      };
    }
  }

  /** Can show Co Applicants for Optional Services */
  public showCoApplicantsForOptionalServices(shareId: number): boolean {
    let selectionCount = 0;
    this.coAppProducts.forEach((applicant) => {
      selectionCount += applicant.shares.filter((f) => f.id === shareId && f.isSelected === true).length;
    });
    return selectionCount > 0;
  }

  // Update isSelected property of Account/Optionsl Servies
  public UpdateBundleIndex(): void {
    this.l360Service.UpdateBundleIndex(this.allProducts).subscribe(() => {
      this.bundleHelperService.refreshBundle();
    });
  }

  /** Saves the user selection to session storage */
  public updateSelection(): void {
    this.statefulService.wrap('ss:oao-accounts-and-services').value = JSON.stringify(this.allProducts);
  }

  /** Check whether lender allowed to select multiple checking account or not (from lender configuration)? . */
  public canDisableAccount(account: Product): boolean {
    const allowOnlyOneSelection = this.isAllowOnlyOneCheckingAccount();
    /** Only disable if the account type os Checking & (already Has Checking Account (or)
     * has Selected Atleaset One Checking Account at Optional Shares Section) */
    if (allowOnlyOneSelection && this.isChecking) {
      const requiredTypeCheckingAccount = this.allProducts.filter((i) => i.type.toLowerCase() === 'checking' && i.isIncluded);
      const hasRequiredTypeCheckingAccount = requiredTypeCheckingAccount && requiredTypeCheckingAccount.length > 0;
      if (hasRequiredTypeCheckingAccount) {
        account.isDisabled = true;
        return true;
      } else {
        const selectedAccounts = this.mainAccount.filter((i) => i.isSelected);
        const canDisable = selectedAccounts.length >= 1 && !account.isSelected ? true : false;
        account.isDisabled = canDisable;
        return canDisable;
      }
    }
    account.isDisabled = false;
    return false;
  }

  private isAllowOnlyOneCheckingAccount() {
    const adminPortalFeatures = this.adminPortalFeaturesService.config;
    return adminPortalFeatures
      ? adminPortalFeatures?.oaoFeatures?.allowOnlyOneCheckingAccount
      : this.lenderConfig?.isAllowOnlyOneCheckingAccount;
  }

  /** Check has coapplicants */
  public hasCoApplicant(): boolean {
    const membershipType = this.statefulService.wrap('api:oaoMemberInformation/membership/membershipTypeName').value;
    if (membershipType && membershipType.toString().toLowerCase() === 'business') {
      const responsibleParties = this.statefulService.arraySize('api:oaoMemberInformation/businessInformation/responsibleParties');
      return responsibleParties && responsibleParties > 1;
    }
    return this.statefulService.wrap('api:oaoMemberInformation/hasCoApplicant').value as boolean;
  }

  /** Check has optional Services */
  public hasCoApplicantShares(indexParent: number): boolean {
    // Check Is any co-applicant has the same share & its optional params
    const hasCoApplicantShares = [];
    this.coAppProducts.forEach((applicant) => {
      if (applicant.shares[indexParent]) {
        hasCoApplicantShares.push(true);
      }
    });
    // eslint-disable-next-line eqeqeq
    return hasCoApplicantShares.length > 0 && hasCoApplicantShares.filter((f) => f == true).length > 0;
  }

  /** Check has optional Services */
  public hasOptionalServices(): boolean {
    const hasOptionalServices = [];
    this.coAppProducts.forEach((applicant) => {
      applicant.shares.forEach((share) => {
        hasOptionalServices.push(share.optionalServices.length > 0 ? true : false);
      });
    });
    return hasOptionalServices.length > 0 && hasOptionalServices.filter((f) => f === true).length > 0;
  }

  public isYouthMembership(): boolean {
    const value = this.statefulService.wrap('api:oaoMemberInformation/membership/isYouth').valueString;
    return value ? JSON.parse(value) : false;
  }

  /** check Joint Applicant availability. If youth membership, no need to check this condition.*/
  public hasJointApplicants(product: Product): boolean {
    if (this.isYouthMembership()) {
      return true;
    } else {
      return product.numberOfJointApplicants && product.numberOfJointApplicants > 0;
    }
  }
}
